import { Icon, MenuItem, Tooltip } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { GetSlidesMetadataResponse } from "apis/presentations/types";
import { ActionPermissionsObject, IWorkspaceResourceFolder, PermissionActionType, PresentationActivityType } from "common/interfaces";
import UpgradeIcon from "js/Components/UpgradeIcon";
import PresentationTemplatesPaneController, { PresentationTemplatesPaneControllerState } from "js/controllers/PresentationTemplatesPaneController";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import getUserProfile from "js/core/services/userProfiles";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { getLastAccessedLabel } from "js/editor/PresentationLibrary/utils/getLastAccessedLabel";
import { t } from "js/i18n";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { Gap5 } from "js/react/components/Gap";
import { IconMenu } from "js/react/components/UiComponents";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import { useFirebaseUser } from "js/react/hooks/useUser";
import { thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";
import { EmptySlideThumbnail, SlideThumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import {
    PresentationTemplateCard,
    PresentationTemplateHeader,
    PresentationTemplateSlideRail,
    PresentationTemplateSlideRailContainer,
} from "js/react/views/SharedResources/Components/PresentationTemplateCardStyles";
import { SharedResourceContextList } from "js/react/views/SharedResources/Components/SharedResourceContextList";
import {
    TEAM_RESOURCE_DESCRIPTION_TOOLTIP_CLASS,
    TeamResourceTooltipStyles,
    TeamResourceUsefulnessButton,
} from "js/react/views/SharedResources/Components/TeamResourceThumbnailCard";
import TeamTemplateSettingsDialog from "js/react/views/TeamResources/dialogs/TeamTemplateSettingsDialog";

const logger = getLogger(LogGroup.WORKSPACES);

const TemplateTitleGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
`;

const TemplateNameAndInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex: 0 1 auto;
`;

const TemplateActions = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 58%;
`;

const TemplateActionButtons = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const PublishLabel = styled.div`
    padding: 2px 6px;
    color: #FF4500;
    background: #FDE5D5;
    border: none;
    border-radius: 4px;
    text-transform: uppercase;
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 0.5px;
`;

const InfoIcon = styled.span<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: ${props => props.$active ? "#11A9E2" : "#CCC"};

    .MuiIcon-root {
        color: inherit;
        font-size: 16px;
    }
`;

const StyledIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
        width: 24px;
        height: 24px;
        padding: 1px;
        border-radius: 50%;
        border: none;
        background: #F2F4F6;
        color: #666;

        &:hover {
            background: #E6EEF3;
            color: #11A9E2;
        }

        .MuiIcon-root {
            color: inherit;
        }
    }
`;

export interface PresentationTemplateItemProps extends PresentationTemplatesPaneControllerState {
    record: PresentationTemplatesPaneControllerState["data"][number];
    editable: boolean;
    onVisibleChange: (isVisible: boolean, presentationId: string) => void;
    onSelect: (presentationId: string | null, isDoubleClick?: boolean) => void;
    onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseUp?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMenuClick?: (presentationId: string) => void;
    selected: boolean;
    selectedFolderId?: string | null;
    onRemoveFromFolder?: (presentationId: string) => void;
    getPresentationSlidesMetadata: (presentationId: string) => Promise<GetSlidesMetadataResponse>;
    resourceFolders?: IWorkspaceResourceFolder[];
    onNavigateToFolder?: (filter: { folderId: string; subFolderId?: string }) => void;
}

export const PresentationTemplateItem = PresentationTemplatesPaneController.withState(function PresentationTemplateItem(props: PresentationTemplateItemProps) {
    const {
        record,
        editable,
        onSelect,
        onMouseDown,
        onMouseUp,
        onMenuClick,
        selected,
        selectedFolderId,
        onRemoveFromFolder,
        resourceFolders = [],
        onNavigateToFolder,
        tags,
        getPresentationSlidesMetadata,
        onVisibleChange
    } = props;

    const { firebaseUser } = useFirebaseUser();
    const [slidesMetadata, setSlidesMetadata] = useState<GetSlidesMetadataResponse>({});
    const [createdByName, setCreatedByName] = useState("");
    const [optimisticLikedByUser, setOptimisticLikedByUser] = useState<boolean | null>(null);
    const [optimisticRatedByCount, setOptimisticRatedByCount] = useState<number | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);

    const isIntersecting = useIntersectionObserver(containerRef);

    const presentation = record.document;
    const resource = record.resource;

    useEffect(() => {
        onVisibleChange(isIntersecting, presentation.id);
    }, [isIntersecting, onVisibleChange, presentation.id]);

    const slideIds: string[] = new Array(Object.keys(presentation.slideRefs).length);
    Object.entries(presentation.slideRefs).forEach(([slideId, idx]) => {
        slideIds[idx] = slideId;
    });

    useEffect(() => {
        if (!isIntersecting) {
            return;
        }

        let isMounted = true;

        (async () => {
            try {
                const [profile, nextSlidesMetadata] = await Promise.all([
                    getUserProfile(presentation.userId),
                    getPresentationSlidesMetadata(presentation.id),
                ]);

                if (!isMounted) {
                    return;
                }

                setCreatedByName(profile.displayName || profile.email || t("Unknown"));
                setSlidesMetadata(nextSlidesMetadata);
            } catch (err) {
                logger.error(err, "[PresentationTemplateItem] Error fetching created by name", { id: presentation.id });
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [isIntersecting, presentation.userId, presentation.id, getPresentationSlidesMetadata]);

    useEffect(() => {
        setOptimisticLikedByUser(null);
        setOptimisticRatedByCount(null);
    }, [resource.metadata.ratedByUsers]);

    const handleClick = () => {
        if (onMouseDown) {
            return;
        }

        onSelect?.(presentation.id, false);
    };

    const handleDoubleClick = () => {
        onSelect?.(presentation.id, true);
    };

    const handleDeletePresentationTemplate = async item => {
        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to delete this template?"),
            message: t("This action can not be undone.")
        })) {
            try {
                await WorkspaceController.deleteWorkspaceResource({
                    resourceId: resource.resourceId,
                    resourceType: resource.resourceType,
                });
            } catch (err) {
                logger.error(err, "[PresentationTemplateItem] Error deleting template", { id: presentation.id });

                ShowErrorDialog({
                    title: t("Unable to delete template"),
                    message: err.message || t("An error occurred while deleting the template. Please try again.")
                });
            }
        }
    };

    const handleOpenPresentationEditor = () => {
        AppController.showEditor({ presentationId: resource.resourceId, folderId: selectedFolderId });
    };

    const handleUpdateTemplateSettings = async (updatedSettings: { description?: string, isPublished?: boolean, name?: string, tags?: string[] }) => {
        if (_.isEmpty(updatedSettings)) {
            return;
        }

        await withProgressDialog({
            title: t("Updating template settings..."),
            action: async () => {
                try {
                    const metadata = _.pickBy({
                        description: updatedSettings.description,
                        isPublished: updatedSettings.isPublished,
                        name: updatedSettings.name
                    }, value => value !== undefined);

                    const tags = updatedSettings.tags;

                    const updates = {
                        resourceId: resource.resourceId,
                        resourceType: resource.resourceType,
                        ...(!_.isEmpty(metadata) && { metadata }),
                        ...(tags && { tags })
                    };

                    await WorkspaceController.updateWorkspaceResource(updates);
                } catch (err) {
                    logger.error(err, "[PresentationTemplateItem] Error updating template settings", { id: presentation.id });

                    ShowErrorDialog({
                        title: t("Unable to edit template settings"),
                        message: err.message
                    });
                }
            }
        });
    };

    const handleEditTemplateSettings = () => {
        ShowDialog(TeamTemplateSettingsDialog, {
            item: {
                name: resource.metadata.name,
                description: resource.metadata.description,
                createdAt: resource.createdAt,
                modifiedAt: presentation.modifiedAt,
                tags: resource.tags,
                createdById: presentation.userId,
                modifiedById: presentation.modifiedBy,
                ratedByCount: resource.metadata.ratedByUsers.length,
                isPublished: resource.metadata.isPublished,
                sips: slideIds,
            },
            existingTags: tags.map(tag => tag.tag),
            editTemplate: handleOpenPresentationEditor,
            updateTemplate: handleUpdateTemplateSettings
        });
    };

    const handlePublish = async () => {
        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to publish this Presentation Template to your team?"),
            message: t("Your team members will be able to create a new presentation starting from this template by selecting it in Create New Presentation dialog.")
        })) {
            try {
                await WorkspaceController.updateWorkspaceResource({
                    resourceId: resource.resourceId,
                    resourceType: resource.resourceType,
                    metadata: {
                        isPublished: true
                    }
                });
            } catch (err) {
                logger.error(err, "[PresentationTemplateItem] Error publishing template", { id: presentation.id });

                ShowErrorDialog({
                    title: t("Unable to update template"),
                    message: err.message
                });
            }
        }
    };

    const handleUnpublish = async () => {
        try {
            await WorkspaceController.updateWorkspaceResource({
                resourceId: resource.resourceId,
                resourceType: resource.resourceType,
                metadata: {
                    isPublished: false
                }
            });
        } catch (err) {
            logger.error(err, "[PresentationTemplateItem] Error unpublishing template", { id: presentation.id });

            ShowErrorDialog({
                title: t("Unable to update template"),
                message: err.message
            });
        }
    };

    const handleToggleLike = async () => {
        const { ratedByUsers } = resource.metadata;
        const likedByUser = optimisticLikedByUser ?? ratedByUsers.includes(firebaseUser.uid);
        const ratedByCount = optimisticRatedByCount ?? ratedByUsers.length;

        setOptimisticLikedByUser(!likedByUser);
        setOptimisticRatedByCount(Math.max(0, ratedByCount + (likedByUser ? -1 : 1)));

        const action = ratedByUsers.includes(firebaseUser.uid) ? "unlike" : "like";

        try {
            await WorkspaceController.updateWorkspaceResourceRating({
                resourceId: resource.resourceId,
                resourceType: resource.resourceType,
                action
            });
        } catch (err) {
            setOptimisticLikedByUser(null);
            setOptimisticRatedByCount(null);
            logger.error(err, "[PresentationTemplateItem] Error toggling like", { id: presentation.id });

            ShowErrorDialog({
                title: t("Unable to update template"),
                message: err.message
            });
        }
    };

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const isPublished = resource.metadata.isPublished;
    const description = resource.metadata.description;
    const name = resource.metadata.name;

    const ratedByCount = resource.metadata.ratedByUsers.length;
    const displayedRatedByCount = optimisticRatedByCount ?? ratedByCount;
    const likedByUser = optimisticLikedByUser ?? resource.metadata.ratedByUsers.includes(firebaseUser.uid);
    const usefulnessActiveLabel = t("You upvoted this template as useful. Click to remove your upvote.");
    const usefulnessInactiveLabel = t("Upvote this template to let your team know it's useful.");
    const lastActivity = resource.metadata.lastActivityByType?.[PresentationActivityType.EDITED];
    const lastActivityUser = lastActivity?.userId ? WorkspaceController.workspaceUsers.find(user => user.uid === lastActivity.userId) : null;
    const lastAccessedLabel = getLastAccessedLabel({
        id: presentation.id,
        name: presentation.name,
        createdAt: presentation.createdAt,
        modifiedAt: Math.max(presentation.modifiedAt, presentation.finishedEditingAt ?? 0),
        slideIds,
        firstSlideModifiedAt: presentation.firstSlideModifiedAt,
        slideCount: slideIds.length,
        trashedAt: presentation.softDeletedAt,
        permissions: {
            owner: false,
            write: false,
            read: true,
        },
        workspaceId: resource.workspaceId,
        isTemplate: true,
        isPublished,
        ownerUid: presentation.userId ?? "",
        ownerName: createdByName || t("Unknown"),
        ownerPhotoURL: "",
        ratedByUsers: resource.metadata.ratedByUsers,
        metadataGeneratedAt: Date.now(),
        isFromRecommendedTemplate: !!presentation.metadata?.isFromRecommendedTemplate,
        finishedEditingAt: presentation.finishedEditingAt,
        ...(lastActivity && {
            lastActivity: {
                userId: lastActivity.userId,
                userName: lastActivityUser?.displayName || lastActivityUser?.email || "",
                activity: lastActivity.activity,
                createdAt: lastActivity.createdAt,
            }
        })
    });
    return (
        <PresentationTemplateCard
            key={presentation.id}
            data-item-id={presentation.id}
            className={selected ? "selected" : ""}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            ref={containerRef}
        >
            <PresentationTemplateHeader>
                <TemplateTitleGroup>
                    <TemplateNameAndInfo>
                        <TruncatedTooltip title={name} enterDelay={500} enterNextDelay={500}>
                            <div className="title">{name}</div>
                        </TruncatedTooltip>
                        <Tooltip
                            title={(
                                <div style={{ padding: 10 }}>
                                    {description || t("No Description")}
                                </div>
                            )}
                            classes={{ popper: "popper-white rounded-5", tooltip: TEAM_RESOURCE_DESCRIPTION_TOOLTIP_CLASS }}
                            placement="top"
                            arrow
                            interactive
                        >
                            <InfoIcon
                                $active={!!description}
                                onMouseDown={event => event.stopPropagation()}
                                onClick={event => event.stopPropagation()}
                            >
                                <Icon className="material-symbols-outlined">info</Icon>
                            </InfoIcon>
                        </Tooltip>
                    </TemplateNameAndInfo>
                    {editable && !isPublished && (
                        <PublishLabel onClick={event => {
                            event.stopPropagation();
                            handlePublish();
                        }}>
                            {t("Draft")}
                        </PublishLabel>
                    )}
                    <TeamResourceTooltipStyles />
                </TemplateTitleGroup>
                <TemplateActions
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}
                    onDoubleClick={event => event.stopPropagation()}
                >
                    <SharedResourceContextList
                        activityLabel={lastAccessedLabel?.label}
                        activityTooltip={lastAccessedLabel?.tooltipLabel}
                        resource={resource}
                        resourceFolders={resourceFolders}
                        onNavigateToFolder={onNavigateToFolder ? ({ folderId, parentFolderId }) => {
                            onNavigateToFolder({
                                folderId: parentFolderId ?? folderId,
                                subFolderId: parentFolderId ? folderId : undefined,
                            });
                        } : undefined}
                    />
                    <TemplateActionButtons>
                        <TeamResourceUsefulnessButton
                            active={likedByUser}
                            count={displayedRatedByCount}
                            visible
                            activeLabel={usefulnessActiveLabel}
                            inactiveLabel={usefulnessInactiveLabel}
                            onClick={handleToggleLike}
                            variant="inline"
                        />
                        {
                            editable &&
                            <StyledIconMenu onClickCapture={() => onMenuClick?.(presentation.id)}>
                                {canUpgradeToManageSharedResources && [
                                    <MenuItem key="settings" disabled divider><UpgradeIcon /><Gap5 />{t("Template Settings...")}</MenuItem>,
                                    <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Template...")}</MenuItem>,
                                    isPublished && <MenuItem key="unpublish" disabled divider><UpgradeIcon /><Gap5 />{t("Unpublish Template")}</MenuItem>,
                                    !isPublished && <MenuItem key="publish" disabled divider><UpgradeIcon /><Gap5 />{t("Publish Template")}</MenuItem>,
                                ].filter(Boolean)}
                                {!canUpgradeToManageSharedResources && canManageSharedResources && [
                                    <MenuItem key="settings" divider onClick={handleEditTemplateSettings}><Icon>label</Icon>{t("Template Settings...")}</MenuItem>,
                                    <MenuItem key="edit" divider onClick={handleOpenPresentationEditor}><Icon>edit</Icon>{t("Edit Template...")}</MenuItem>,
                                    isPublished && <MenuItem key="unpublish" divider onClick={handleUnpublish}><Icon>unpublished</Icon>{t("Unpublish Template")}</MenuItem>,
                                    !isPublished && <MenuItem key="publish" divider onClick={handlePublish}><Icon>published_with_changes</Icon>{t("Publish Template")}</MenuItem>,
                                ].filter(Boolean)}
                                {selectedFolderId && onRemoveFromFolder && (
                                    <MenuItem key="remove-from-folder" divider onClick={() => onRemoveFromFolder(presentation.id)}>
                                        <Icon>remove_circle</Icon>{t("Remove from Folder")}
                                    </MenuItem>
                                )}
                                <MenuItem key="delete" onClick={handleDeletePresentationTemplate}><Icon>delete</Icon>{t("Delete Template")}</MenuItem>
                            </StyledIconMenu>
                        }
                    </TemplateActionButtons>
                </TemplateActions>
            </PresentationTemplateHeader>
            <PresentationTemplateSlideRailContainer>
                <PresentationTemplateSlideRail style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                    {!isIntersecting ? (
                        <EmptySlideThumbnail />
                    ) : slideIds.map(slideId => {
                        const modifiedAt = slidesMetadata[slideId]?.modifiedAt;
                        if (!modifiedAt) {
                            return <EmptySlideThumbnail key={slideId} />;
                        }

                        return (
                            <SlideThumbnail
                                key={slideId}
                                slideId={slideId}
                                presentationOrLinkId={presentation.id}
                                modifiedAt={modifiedAt}
                                readOnly
                            />
                        );
                    })}
                </PresentationTemplateSlideRail>
            </PresentationTemplateSlideRailContainer>
        </PresentationTemplateCard>
    );
});

const PresentationTemplateItemSkeletonContainer = styled.div`
    width: 100%;
    margin-bottom: 12px;

    >span {
        transform: none;
        height: 166px;
        border-radius: 4px;
    }
`;

export function PresentationTemplateItemSkeleton() {
    return (
        <PresentationTemplateItemSkeletonContainer>
            <Skeleton />
        </PresentationTemplateItemSkeletonContainer>
    );
}
